
import { useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import AuthContext from "../Context/AuthProvider";
import { getFacilityDetails, updateFacility } from "../API/FacilitySettingsApi";
import { Grid2, Card, Button, TextField, Tooltip, Typography } from '@mui/material';
import { EditOutlined } from "@mui/icons-material";
import { IconButton } from "@mui/material";
import { PhotoCamera, Delete } from "@mui/icons-material";
import { useSnackbar } from "../Utility/UserMessageContext";

function FacilityBasicSettings() {
  const auth = useContext(AuthContext);
  const location = useLocation();
  const { showSnackbar } = useSnackbar();
  const [editMode, setEditMode] = useState(false);
  const [facilityToEdit, setFacilityToEdit] = useState();
  const [facilityName, setFacilityName] = useState();
  const [mobileNumber, setMobileNumber] = useState();
  const [emailId, setEmailId] = useState();
  const [addressLine1, setAddressLine1] = useState();
  const [addressLine2, setAddressLine2] = useState();
  const [addressPIN, setAddressPIN] = useState();
  const [addressState, setAddressState] = useState();
  const [addressCity, setAddressCity] = useState();
  const [logoBase64Url, setLogoBase64Url] = useState();

  // Fetch facility details on component mount
  useEffect(() => {
    const loadFacility = async () => {
      const result = await getFacilityDetails(auth);
      if (result?.isSuccess) {
        setFacilityToEdit(result?.data);
        setFacilityName(result?.data?.name);
        setEmailId(result?.data?.email);
        setMobileNumber(result?.data?.mobileNo);
        setAddressLine1(result?.data?.addressLine1);
        setAddressLine2(result?.data?.addressLine2);
        setAddressPIN(result?.data?.pin);
        setAddressState(result?.data?.state);
        setAddressCity(result?.data?.city);
        setLogoBase64Url(result?.data?.logo);
      } else {
        console.log("Failed to fetch facility details:", result?.data);
      }
    };
    loadFacility();
  }, [auth, location?.state]);

  // Save facility details
  const saveFacilityDetails = async (e) => {
    e.preventDefault();

    var form = document.getElementById("basicFacilityDetailsForm");

    if (form.checkValidity()) {
      const facility = {
        name: facilityName,
        emailId: emailId,
        mobileNo: mobileNumber,
        isActive: true,
        logo: logoBase64Url,
        addressLine1: addressLine1,
        addressLine2: addressLine2,
        addressPIN: addressPIN,
        addressState: addressState,
        addressCity: addressCity,
      };

      console.log("Updating facility:", facility); // Debugging

      const result = await updateFacility(auth?.auth, facility);
      
      console.log("Update Facility Response:", result); // Debugging

      if (result?.isSuccess) {
        // Update the local state with the new data
        setFacilityToEdit(result?.data);
        setFacilityName(result?.data?.name);
        setEmailId(result?.data?.email);
        setMobileNumber(result?.data?.mobileNo);
        setAddressLine1(result?.data?.addressLine1);
        setAddressLine2(result?.data?.addressLine2);
        setAddressPIN(result?.data?.pin);
        setAddressState(result?.data?.state);
        setAddressCity(result?.data?.city);
        setLogoBase64Url(result?.data?.logo);

        // Exit edit mode
        setEditMode(false);

        // Show success toast
        showSnackbar("Facility details updated successfully.", "success");
      } else {
        // Show error toast
        showSnackbar("Failed to update facility details. Please try again.", "error");
      }
    }
  };

  // Handle logo change
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoBase64Url(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle logo delete
  const handleLogoDelete = () => {
    setLogoBase64Url(null);
  };

  return (
    <Grid2 container spacing={2} size={12} sx={{ paddingX: "5px", marginY: "10px", alignContent: "center" }}>
      {/* Action Buttons */}
      <Grid2 container size={12}>
        <Grid2 container size={12} alignItems="center" alignContent="center">
          {!editMode ? (
            <Tooltip title="Edit Facility Details" arrow>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => setEditMode(true)}
                size="small"
                startIcon={<EditOutlined fontSize="small" />}
              >
                Edit
              </Button>
            </Tooltip>
          ) : (
            <>
              <Button
                type="submit"
                variant="contained"
                color="secondary"
                size="small"
                form="basicFacilityDetailsForm"
              >
                Save
              </Button>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                onClick={() => setEditMode(false)}
               
              >
                Cancel
              </Button>
            </>
          )}
        </Grid2>
      </Grid2>

      {/* Main Content */}
      <Grid2 container spacing={2} size={12} sx={{ maxHeight: "calc(100vh - 200px)", overflowY: "auto"}}>
        {/* Original Form Card */}
        <Grid2 size={{xs:12, sm:12, md:12, lg:4, xl:4}}>
          <Card variant="outlined">
            {editMode ? (
              <form
                noValidate
                className="was-validated"
                onSubmit={saveFacilityDetails}
                id="basicFacilityDetailsForm"
              >
                <Grid2 container spacing={2} size={12} sx={{ padding: 1 }}>
                  <Grid2 item size={12}> 
                    <TextField
                      label="Name"
                      variant="outlined"
                      margin="dense"
                      size="small"
                      fullWidth
                      value={facilityName}
                      onChange={(e) => setFacilityName(e.target.value)}
                      required
                    />
                  </Grid2>
                  <Grid2 size={12}>
                    <TextField
                      margin="dense"
                      size="small"
                      label="Address Line 1"
                      variant="outlined"
                      fullWidth
                      value={addressLine1}
                      onChange={(e) => setAddressLine1(e.target.value)}
                    />
                  </Grid2>
                  <Grid2 size={12}>
                    <TextField
                      margin="dense"
                      size="small"
                      label="Address Line 2"
                      variant="outlined"
                      fullWidth
                      value={addressLine2}
                      onChange={(e) => setAddressLine2(e.target.value)}
                    />
                  </Grid2>
                  <Grid2 item size={{xs:12, sm:12, md:12, lg:6, xl:6}}>
                    <TextField
                      margin="dense"
                      size="small"
                      label="PIN"
                      variant="outlined"
                      fullWidth
                      value={addressPIN}
                      onChange={(e) => setAddressPIN(e.target.value)}
                    />
                  </Grid2>
                  <Grid2 item size={{xs:12, sm:12, md:12, lg:6, xl:6}}>
                    <TextField
                      margin="dense"
                      size="small"
                      label="State"
                      variant="outlined"
                      fullWidth
                      value={addressState}
                      onChange={(e) => setAddressState(e.target.value)}
                    />
                  </Grid2>
                  <Grid2 item size={{xs:12, sm:12, md:12, lg:6, xl:6}}>
                    <TextField
                      margin="dense"
                      size="small"
                      label="City"
                      variant="outlined"
                      fullWidth
                      value={addressCity}
                      onChange={(e) => setAddressCity(e.target.value)}
                    />
                  </Grid2>
                  <Grid2 item size={{xs:12, sm:12, md:12, lg:6, xl:6}}>
                    <TextField
                      margin="dense"
                      size="small"
                      label="Email Id"
                      variant="outlined"
                      fullWidth
                      value={emailId}
                      onChange={(e) => setEmailId(e.target.value)}
                      required
                    />
                  </Grid2>
                  <Grid2 item size={{xs:12, sm:12, md:12, lg:6, xl:6}}>
                    <TextField
                      margin="dense"
                      size="small"
                      label="Mobile No."
                      variant="outlined"
                      fullWidth
                      value={mobileNumber}
                      onChange={(e) => setMobileNumber(e.target.value)}
                      required
                    />
                  </Grid2>
                </Grid2>
              </form>
            ) : (
              <Grid2 container sx={{ padding: 2 }}>
                <Grid2 container size={{xs:12, sm:12, md:12, lg:3, xl:3}}>
                  <Grid2 container size={12} sx={{ alignItems: "center", justifyContent: "center" }}>
                    <img
                      src={logoBase64Url || "./hospital.png"} 
                      alt="Facility Logo"
                      style={{ borderRadius: "50%" }}
                      height="100"
                      width="100"
                    />
                  </Grid2>
                </Grid2>
                <Grid2 container size={{xs:12, sm:12, md:12, lg:9, xl:9}} sx={{ pl: 2 }}>
                  <Grid2 size={12}>
                    <Typography variant="h6">{facilityName}</Typography>
                  </Grid2>
                  <Grid2 size={12}>
                    <Typography sx={{ textDecoration: 'underline' }}>Address</Typography>
                  </Grid2>
                  <Grid2 size={12}>
                    <Typography>{addressLine1},&nbsp;{addressLine2}</Typography>
                  </Grid2>
                  <Grid2 size={12}>
                    <Typography>{addressCity},&nbsp;{addressState},&nbsp;{addressPIN}</Typography>
                  </Grid2>
                  <Grid2 size={{xs:12, sm:12, md:12, lg:6, xl:6}}>
 
    <Typography sx={{ textDecoration: 'underline' }}>Email</Typography>
  </Grid2>
  <Grid2 size={12}>
    <Typography>{emailId}</Typography>
  </Grid2>
</Grid2>
<Grid2 size={{xs:12, sm:12, md:12, lg:6, xl:6}} sx={{ pl: 15.5 }}>
  <Grid2 size={12}>
    <Typography sx={{ textDecoration: 'underline' }}>Mobile No</Typography>
  </Grid2>
  <Grid2 size={12}>
    <Typography>{mobileNumber}</Typography>
  </Grid2>

                </Grid2>
              </Grid2>
            )}
          </Card>
        </Grid2>

        {/* Photo Card - Only in Edit Mode */}
        {editMode && (
          <Grid2
            size={{xs:8, sm:8, md:8, lg:2, xl:2}}
           >
            <Card variant="outlined" >
              <Grid2 container spacing={1} sx={{ padding: 1 }} direction="column" alignItems="center">
                <Grid2>
                  <img
                    src={logoBase64Url || "./hospital.png"}
                    alt="Facility Logo"
                    width="120"
                    height="120"
                    style={{ borderRadius: '50%' }}
                  />
                </Grid2>
                <Grid2 container justifyContent="center" spacing={1} >
                  <Grid2>
                    <input
                      type="file"
                      accept="image/*"
                      hidden
                      id="logo-upload"
                      onChange={handleLogoChange}
                    />
                    <label htmlFor="logo-upload">
                      <IconButton component="span">
                        <PhotoCamera />
                      </IconButton>
                    </label>
                  </Grid2>
                  <Grid2>
                    <IconButton onClick={handleLogoDelete}>
                      <Delete />
                    </IconButton>
                  </Grid2>
                </Grid2>
              </Grid2>
            </Card>
          </Grid2>
        )}
      </Grid2>
    </Grid2>
  );
}

export default FacilityBasicSettings;